'use client';

import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { Box } from '@terra/ui-styled-system';

import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { MainContentArea } from './MainContentArea';
import { ContentChatLayout } from './ContentChatLayout';
import { BottomRibbon } from './BottomRibbon';
import { GlobalContextMenu } from './GlobalContextMenu';
import { UpdateNotification } from '../UpdateNotification';
import { GlobalSearchDialog } from '../search/GlobalSearchDialog';
import { ConfigureCredentialsDialog } from '../dialogs/ConfigureCredentialsDialog';
import { InactivityWarningDialog } from '../../app/components/InactivityWarningDialog';
import { SessionDebugMenu } from '../debug/SessionDebugMenu';
import { TabScopeProvider } from '../../contexts/TabScopeContext';
import {
  SIDEBAR_WIDTH_EXPANDED,
  SIDEBAR_WIDTH_MINIMIZED,
} from './sidebarWidth';

import {
  useMainContentTabs,
  usePanelVisibility,
  useSidebarMinimized,
} from '../../store/layoutStore';
import { useTargetId, useTargetType } from '../../store/projectStore';
import { buildTabScopeKey } from '../../store/tab/tab-scope-key';
import { useIsAiApplicationKindPending } from '../../hooks/useIsAiApplicationKindPending';
import { isAppTarget, TARGET_TYPE } from '../../utils/target-scope.utils';
import { isDomainTreeFilterTab } from '../../utils/domainTreeQueryOptions';

import { useWelcomeTabManager } from '../../hooks/useWelcomeTabManager';
import { useAutoSelectTarget } from '../../hooks/useAutoSelectTarget';
import { useDecoderKeyboard } from '../../hooks/useDecoderKeyboard';
import { useReplayKeyboard } from '../../hooks/useReplayKeyboard';
import { useTabKeyboard } from '../../hooks/useTabKeyboard';
import { useTabNavigation } from '../../hooks/useTabNavigation';
import { useSearchKeyboard } from '../../hooks/useSearchKeyboard';
import { useProxyAutoUpdate } from '../../hooks/useProxyAutoUpdate';
import { useMatchReplaceRulesHydration } from '../../hooks/useMatchReplaceRulesHydration';
import { useInterceptRulesHydration } from '../../hooks/useInterceptRulesHydration';
import { useAutoUpdater } from '../../hooks/useAutoUpdater';

import { useElectronMenuCommands } from '../../hooks/layout/useElectronMenuCommands';
import { useSyncBoothTourElectronMenu } from '../../hooks/layout/useSyncBoothTourElectronMenu';
import { useSearchDialogNavigation } from '../../hooks/layout/useSearchDialogNavigation';
import { useManualTrackWithCredentials } from '../../hooks/useManualTrackWithCredentials';
import { useTargetChangeSync } from '../../hooks/useTargetChangeSync';
import { useRecordRecentItems } from '../../hooks/useRecordRecentItems';

type AppLayoutProps = {
  children: ReactNode;
};

export function AppLayout({ children }: AppLayoutProps) {
  const { activeTab, activePageEntry, navigateInActiveTab } =
    useMainContentTabs();

  const { showSidePanels } = usePanelVisibility();
  const { isSidebarMinimized } = useSidebarMinimized();
  const targetId = useTargetId();
  const targetType = useTargetType();
  const isAiApplicationKindPending = useIsAiApplicationKindPending();

  const activeTabScopeId = useMemo(() => {
    if (!activeTab) return undefined;

    return buildTabScopeKey({
      targetId,
      tabId: activeTab.id,
      pageKey: activePageEntry?.pageId ?? activeTab.id,
    });
  }, [activeTab, activePageEntry, targetId]);

  const shouldShowDomainTree = useMemo(() => {
    if (!activeTab || !isAppTarget(targetType)) return false;
    if (isAiApplicationKindPending) return false;
    if (
      targetType === TARGET_TYPE.AI_APPLICATION &&
      (activeTab.type === 'signals-list' || activeTab.type === 'assets-list')
    ) {
      return false;
    }

    return isDomainTreeFilterTab(activeTab.type);
  }, [activeTab, targetType, isAiApplicationKindPending]);

  useTargetChangeSync();
  useRecordRecentItems({ activeTab, targetId, targetType });
  useWelcomeTabManager();
  useAutoSelectTarget();
  useDecoderKeyboard();
  useReplayKeyboard();
  useTabKeyboard();
  useTabNavigation();
  const { isSearchOpen, setIsSearchOpen } = useSearchKeyboard();

  useElectronMenuCommands(navigateInActiveTab);
  useSyncBoothTourElectronMenu();
  useProxyAutoUpdate();
  useMatchReplaceRulesHydration();
  useInterceptRulesHydration();
  const autoUpdater = useAutoUpdater();

  const searchNavigation = useSearchDialogNavigation(navigateInActiveTab);

  const {
    isConfigureCredentialsOpen,
    setIsConfigureCredentialsOpen,
    defaultCredentialId,
    runPendingTrack,
  } = useManualTrackWithCredentials();

  return (
    <Box
      h="100vh"
      w="100vw"
      bg="bg.canvas"
      display="flex"
      flexDirection="column"
      overflowX="hidden"
      data-id="app-layout-root"
    >
      <InactivityWarningDialog />
      <ConfigureCredentialsDialog
        isOpen={isConfigureCredentialsOpen}
        onOpenChange={setIsConfigureCredentialsOpen}
        defaultCredentialId={defaultCredentialId}
        onConfirm={runPendingTrack}
      />
      <SessionDebugMenu />
      <GlobalContextMenu />

      <GlobalSearchDialog
        isOpen={isSearchOpen}
        onOpenChange={setIsSearchOpen}
        onNavigateToEndpoint={searchNavigation.handleNavigateToEndpoint}
        onNavigateToSignal={searchNavigation.handleNavigateToSignal}
        onNavigateToRequest={searchNavigation.handleNavigateToRequest}
        onOpenSettings={searchNavigation.handleOpenSettings}
        onOpenHistory={searchNavigation.handleOpenHistory}
        onOpenIntercept={searchNavigation.handleOpenIntercept}
        onOpenReplay={searchNavigation.handleOpenReplay}
      />

      <UpdateNotification autoUpdater={autoUpdater} />

      <Box flexShrink={0} data-id="app-layout-topbar">
        <TopBar />
      </Box>

      <Box
        flex="1"
        overflow="hidden"
        display="flex"
        minW="0"
        data-id="app-layout-main-row"
      >
        <Box
          flexShrink={0}
          transition="width 150ms ease"
          data-id="app-layout-sidebar"
          style={{
            width: isSidebarMinimized
              ? SIDEBAR_WIDTH_MINIMIZED
              : SIDEBAR_WIDTH_EXPANDED,
          }}
        >
          <Sidebar />
        </Box>

        <TabScopeProvider scopeId={activeTabScopeId}>
          <Box
            flex="1"
            minW="0"
            overflow="hidden"
            w="full"
            display="flex"
            position="relative"
            data-id="app-layout-main-content"
          >
            {showSidePanels ? (
              <ContentChatLayout showDomainTree={shouldShowDomainTree}>
                {children}
              </ContentChatLayout>
            ) : (
              <MainContentArea>{children}</MainContentArea>
            )}
          </Box>
        </TabScopeProvider>
      </Box>

      <Box data-id="app-layout-bottom-ribbon" h="8" flexShrink={0}>
        <BottomRibbon autoUpdater={autoUpdater} />
      </Box>
    </Box>
  );
}
